import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import useCurrentProfit from 'hooks/useCurrentProfit';
import useYieldProfit from 'hooks/useYieldProfit';
import useCurrentLoss from 'hooks/useCurrentLoss';
import useNpkProfit from 'hooks/useNpkProfit';
import useGainsOm from 'hooks/useGainsOm';
import useImprovementInCost from 'hooks/useImprovementInCost';
import useCurrency from 'hooks/useCurreny';
import {
  formatToTwoDecimals,
  getCellFontColor,
  unMaskInputValue,
} from 'utils/helpers';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { formatSwissNumber } from 'utils/formatNumber';

interface Props {
  profitTitle: string;
  setLossTitle: (value: string) => void;
  hasErrors?: boolean;
  isMaintenanceMode?: boolean;
  isGreaterTarget?: boolean;
}

const FiveYearsBoardProfit = ({
  profitTitle,
  setLossTitle,
  hasErrors,
}: Props) => {
  const { t } = useTranslation();
  const { currencyGreen } = useCurrency();
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size for responsive tooltip width
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const {
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
    boardCropValues,
    fertilizationValues,
    onlyAc,
  } = useCroptimizer();

  const {
    getY1CurrentProfit,
    getY2CurrentProfit,
    getY3CurrentProfit,
    getY4CurrentProfit,
    getY5CurrentProfit,
  } = useCurrentProfit({
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
  });

  const {
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
  } = useCurrentLoss({
    boardStatusValues,
    mineralizationValues,
  });

  const {
    getY1YieldProfit,
    getY2YieldProfit,
    getY3YieldProfit,
    getY4YieldProfit,
    getY5YieldProfit,
    getTotalYieldProfit,
  } = useYieldProfit({
    boardStatusValues,
    boardCropValues,
    getY1CurrentProfit,
    getY2CurrentProfit,
    getY3CurrentProfit,
    getY4CurrentProfit,
    getY5CurrentProfit,
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
    boardSoilValues,
  });

  const {
    getY1NpkProfit,
    getY2NpkProfit,
    getY3NpkProfit,
    getY4NpkProfit,
    getY5NpkProfit,
    totalNpKProfit,
  } = useNpkProfit({
    boardStatusValues,
    fertilizationValues,
    getY1CurrentProfit,
    getY2CurrentProfit,
    getY3CurrentProfit,
    getY4CurrentProfit,
    getY5CurrentProfit,
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
    boardSoilValues,
  });

  const {
    useGainsOm1,
    useGainsOm2,
    useGainsOm3,
    useGainsOm4,
    useGainsOm5,
    totalOmGains,
  } = useGainsOm({
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
    fertilizationValues,
    getY1CurrentProfit,
    getY2CurrentProfit,
    getY3CurrentProfit,
    getY4CurrentProfit,
    getY5CurrentProfit,
    getY1CurrentLoss,
    getY2CurrentLoss,
    getY3CurrentLoss,
    getY4CurrentLoss,
    getY5CurrentLoss,
  });

  const { useImprovementInCostResult } = useImprovementInCost({
    boardStatusValues,
    boardSoilValues,
  });

  const returnOnInvestment = useMemo(
    () => [
      getTotalYieldProfit,
      totalNpKProfit,
      totalOmGains,
      useImprovementInCostResult,
    ].reduce((acc: number, curr) => acc + (curr ? Number(curr) : 0), 0),
    [
      getTotalYieldProfit,
      totalNpKProfit,
      totalOmGains,
      useImprovementInCostResult,
    ],
  );

  useEffect(() => {
    if (!hasErrors && boardStatusValues?.targetPh) {
      const targetPhValue = formatToTwoDecimals(unMaskInputValue(boardStatusValues.targetPh));
      const title = `Comparé au pH visé de ${targetPhValue}`;
      setLossTitle(title);
    }
  }, [boardStatusValues?.targetPh, hasErrors, setLossTitle]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'firstName',
        header: () => (
          <div className='font-bold  text-black'>
            <p>Comparé à la même année sans apport :</p>
            {profitTitle}
          </div>
        ),
        footer: 'firstName',
        size: 800,
        cell: (row: any) => {
          if (row.row.id === '5') {
            return (
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                {row.getValue()}
              </div>
            );
          } else {
            return <div>{row.getValue()}</div>;
          }
        },
      },
      {
        accessorKey: 'yOne',
        header: 'N1',
        footer: 'firstName',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yTwo',
        header: 'N2',
        footer: 'firstName',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yThree',
        header: 'N3',
        footer: 'firstName',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yFour',
        header: 'N4',
        footer: 'firstName',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'yFive',
        header: 'N5',
        footer: 'firstName',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else {
            return (
              <div style={{ color: getCellFontColor(row.getValue()) }}>
                {row.getValue()}
              </div>
            );
          }
        },
      },
      {
        accessorKey: 'total',
        header: 'Total',
        footer: 'firstName',
        cell: (row: any) => {
          if (row.row.id === '0') {
            return <div>{row.getValue()}</div>;
          } else if (row.getValue() > 0) {
            return (
              <div
                style={{
                  color: getCellFontColor(row.getValue()),
                  fontWeight: 700,
                }}
              >
                {row.getValue()}
              </div>
            );
          } else {
            return (
              <div
                style={{
                  color: getCellFontColor(row.getValue()),
                  fontWeight: 700,
                }}
              >
                {row.getValue()}
              </div>
            );
          }
        },
      },
    ],
    [profitTitle],
  );

  const previousDataRef = useRef<any[] | null>(null);

  const data = useMemo(
    () => {
      // If there are errors, return previous data without recalculating
      // This prevents any calculations from running
      if (hasErrors && previousDataRef.current) {
        return previousDataRef.current;
      }

      // Calculate running cumulative totals for each year column
      // Year 1 = sum of Y1 values
      const cumulativeY1 = 
        Number((getY1YieldProfit || 0)) + 
        (getY1NpkProfit || 0) + 
        Number(useGainsOm1 || 0);
      
      // Year 2 = sum of Y1 + Y2 values
      const cumulativeY2 = 
        Number((getY1YieldProfit || 0)) + 
        (getY1NpkProfit || 0) + 
        Number(useGainsOm1 || 0) +
        Number((getY2YieldProfit || 0)) + 
        (getY2NpkProfit || 0) + 
        Number(useGainsOm2 || 0);
      
      // Year 3 = sum of Y1 + Y2 + Y3 values
      const cumulativeY3 = 
        Number((getY1YieldProfit || 0)) + 
        (getY1NpkProfit || 0) + 
        Number(useGainsOm1 || 0) +
        Number((getY2YieldProfit || 0)) + 
        (getY2NpkProfit || 0) + 
        Number(useGainsOm2 || 0) +
        Number((getY3YieldProfit || 0)) + 
        (getY3NpkProfit || 0) + 
        Number(useGainsOm3 || 0);
      
      // Year 4 = sum of Y1 + Y2 + Y3 + Y4 values
      const cumulativeY4 = 
        Number((getY1YieldProfit || 0)) + 
        (getY1NpkProfit || 0) + 
        Number(useGainsOm1 || 0) +
        Number((getY2YieldProfit || 0)) + 
        (getY2NpkProfit || 0) + 
        Number(useGainsOm2 || 0) +
        Number((getY3YieldProfit || 0)) + 
        (getY3NpkProfit || 0) + 
        Number(useGainsOm3 || 0) +
        Number((getY4YieldProfit || 0)) +
        (getY4NpkProfit || 0) + 
        Number(useGainsOm4 || 0);
      
      // Year 5 = same as total (use returnOnInvestment directly to avoid rounding discrepancies)
      const cumulativeY5 = returnOnInvestment;

      // Calculate new data
      const newData = [
        {
          firstName: t('soilPhachieved'),
          yOne: formatToTwoDecimals(unMaskInputValue(getY1CurrentProfit)),
          yTwo: formatToTwoDecimals(unMaskInputValue(getY2CurrentProfit ?? '')),
          yThree: formatToTwoDecimals(unMaskInputValue(getY3CurrentProfit ?? '')),
          yFour: formatToTwoDecimals(unMaskInputValue(getY4CurrentProfit ?? '')),
          yFive: formatToTwoDecimals(unMaskInputValue(getY5CurrentProfit ?? '')),
          total: '-',
        },

        {
          firstName: t('yieldGains') + ` (${currencyGreen}/${onlyAc})`,
          yOne: getY1YieldProfit,
          yTwo: getY2YieldProfit,
          yThree: getY3YieldProfit,
          yFour: getY4YieldProfit,
          yFive: getY5YieldProfit,
          total: formatSwissNumber(getTotalYieldProfit),
        },

        {
          firstName: t('npkGains') + ` (${currencyGreen}/${onlyAc})`,
          yOne: getY1NpkProfit,
          yTwo: getY2NpkProfit,
          yThree: getY3NpkProfit,
          yFour: getY4NpkProfit,
          yFive: getY5NpkProfit,          
          total: formatSwissNumber(totalNpKProfit || 0),
        },

        {
          firstName: t('gainsmonps') + ` (${currencyGreen}/${onlyAc})`,
          yOne: Math.round(Number(useGainsOm1)),
          yTwo: Math.round(Number(useGainsOm2)),
          yThree: Math.round(Number(useGainsOm3)),
          yFour: Math.round(Number(useGainsOm4)),
          yFive: Math.round(Number(useGainsOm5)),
          total: formatSwissNumber(totalOmGains),
        },

        // INFO
        // CrOptimizer-Simplification task
        // Removed one line related to product cost

        /* {
          firstName:
            t('improvementincost') +
            ` ${currencyGreen}/${onlyAc}/5 ${t('years')}`,
          total: formatSwissNumber(useImprovementInCostResult),
        }, */

        {
          firstName:
            t('returnoninvestment') +
            ` (${currencyGreen}/${onlyAc}) `,
          yOne: formatSwissNumber(cumulativeY1),
          yTwo: formatSwissNumber(cumulativeY2),
          yThree: formatSwissNumber(cumulativeY3),
          yFour: formatSwissNumber(cumulativeY4),
          yFive: formatSwissNumber(cumulativeY5),
          total: formatSwissNumber(returnOnInvestment),
        },
      ];

      // Save new data for future use
      previousDataRef.current = newData;

      return newData;
    },
    // Early return prevents calculations from running when hasErrors
    [
      hasErrors,
      getY1CurrentProfit,
      getY2CurrentProfit,
      getY3CurrentProfit,
      getY4CurrentProfit,
      getY5CurrentProfit,
      currencyGreen,
      onlyAc,
      getY1YieldProfit,
      getY2YieldProfit,
      getY3YieldProfit,
      getY4YieldProfit,
      getY5YieldProfit,
      getTotalYieldProfit,
      getY1NpkProfit,
      getY2NpkProfit,
      getY3NpkProfit,
      getY4NpkProfit,
      getY5NpkProfit,
      totalNpKProfit,
      useGainsOm1,
      useGainsOm2,
      useGainsOm3,
      useGainsOm4,
      useGainsOm5,
      totalOmGains,
      returnOnInvestment,
      t,
    ],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div>
      <div className="text-black font-bold text-lg md:text-base md:mt-6 mb-4">
        {t('potentialProfitFiveYears')}{' '}
        <span
          className="cursor-pointer font-black relative inline-block"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          ref={tooltipRef}
        > 
            ⓘ
          {showTooltip && (
            <div
              className="absolute left-1/2 transform -translate-x-1/2 bg-white shadow-lg"
              style={{
                top: '100%',
                width: isMobile ? '65vw' : '650px',
                maxWidth: '80vw',
                maxHeight: '600px',
                padding: '16px',
                border: '1px solid #cbcbcb',
                borderRadius: '12px',
                textAlign: 'center',
                marginBottom: '15px',
                zIndex: 1000,
                fontWeight: 400,
              }}
            >
              <div style={{ maxWidth: '100%',
                maxHeight: '600px' }}>
                <div style={{
                  marginBottom: '12px',
                  textAlign: 'left',
                  color: '#000',
                  fontSize: '14px',
                }}>
                    Pour obtenir les gains potentiels, apporter {formatSwissNumber(Number(boardSoilValues?.totalMtha))} t de VN efficace / ha en choisissant le bon produit grâce à l&apos;index CAP :
                </div>
                <img
                  src="/5YearsProjGraphFrench.png"
                  style={{
                    height: 'auto',
                    maxHeight: '600px',
                    minHeight: '400px',
                    display: 'block',
                    margin: '0 auto',
                  }}
                  alt="5 Years Projection Graph"
                />
              </div>
            </div>
          )}
        </span>
      </div>

      <div className="overflow-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="text-left">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="border-b border-b-gray-200 py-4 px-1 text-[#757575] font-normal text-[16px]"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="text-left">
                {row.getVisibleCells().map((cell) => (
                  <td
                    style={{
                      width: cell.column.getSize(),
                    }}
                    key={cell.id}
                    className="border-b border-b-gray-200 py-4 px-1 text-[16px]"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="h-4" />
      </div>
    </div>
  );
};

export default FiveYearsBoardProfit;
